/**
 * Used to generate different variations based on dataset length
 */
export const chartColors = [
  ['#23C386'],
  ['#23C386', '#7A3390'],
  ['#23C386', '#2B4697', '#7A3390'],
  ['#23C386', '#2A8F9D', '#2B4697', '#7A3390'],
  ['#23C386', '#2A8F9D', '#2B4697', '#512D93', '#7A3390'],
  ['#23C386', '#2A8F9D', '#285D99', '#2B4697', '#512D93', '#7A3390'],
  ['#23C386', '#2A8F9D', '#285D99', '#2B4697', '#512D93', '#6C2E92', '#7A3390'],
  [
    '#23C386',
    '#2BA691',
    '#2A8F9D',
    '#285D99',
    '#2B4697',
    '#512D93',
    '#6C2E92',
    '#7A3390',
  ],
  [
    '#23C386',
    '#2BA691',
    '#2A8F9D',
    '#285D99',
    '#2B4697',
    '#2E3195',
    '#512D93',
    '#6C2E92',
    '#7A3390',
  ],
  [
    '#23C386',
    '#29B587',
    '#2BA691',
    '#2A8F9D',
    '#285D99',
    '#2B4697',
    '#2E3195',
    '#512D93',
    '#6C2E92',
    '#7A3390',
  ],
  [
    '#23C386',
    '#29B587',
    '#2BA691',
    '#2A8F9D',
    '#297E9C',
    '#285D99',
    '#2B4697',
    '#2E3195',
    '#512D93',
    '#6C2E92',
    '#7A3390',
  ],
  [
    '#23C386',
    '#29B587',
    '#2BA691',
    '#2A8F9D',
    '#297E9C',
    '#296E9A',
    '#285D99',
    '#2B4697',
    '#2E3195',
    '#512D93',
    '#6C2E92',
    '#7A3390',
  ],
]

/**
 * Grabs a specific color array from above based on dataset length
 *
 * @param {Integer} length
 */
export function getChartColors(length) {
  return chartColors[length - 1]
}

export const styles = {
  font: {
    sans: "IBM Plex Sans, 'Helvetica Neue', Arial, sans-serif",
    mono: 'IBM Plex Mono, monospace',
  },
}

export function getDonutVariants(type) {
  // eslint-disable-next-line default-case
  switch (type) {
    case 'donut':
      return {
        valueFormat: (value) => `${value}%`,
      }
  }
}

export function getBarVariants(type, graphOverrides) {
  // eslint-disable-next-line default-case
  switch (type) {
    case 'vertical':
      return {
        layout: 'vertical',
        enableGridX: false,
        enableGridY: true,
        innerPadding: 2,
        margin: {
          left: graphOverrides.noAxisLeft ? 0 : 35,
          bottom: 10,
          top: 10,
        },

        // eslint-disable-next-line react/prop-types
        tooltip: ({ indexValue, value, color }) => (
          <div
            css={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: 'auto',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div css={{ width: 12, height: 12, background: color }} />
            <div>
              {graphOverrides.noPercentageLabels
                ? `${indexValue}: ${value}`
                : `${indexValue}: ${value}%`}
            </div>
          </div>
        ),
        ...(graphOverrides.noPercentageAxis
          ? {}
          : {
              axisLeft: {
                format: (value) => `${value}%`,
              },
            }),
        ...(graphOverrides.noPercentageLabels
          ? {}
          : { label: (node) => `${node.value}%` }),
      }
    case 'horizontal-grouped':
      return {
        groupMode: 'grouped',
        layout: 'horizontal',
        enableGridX: true,
        enableGridY: false,
        innerPadding: 2,
        margin: { right: 10, top: 10 },
        // eslint-disable-next-line react/prop-types
        tooltip: ({ indexValue, value, color, id }) => (
          <div
            css={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: 'auto',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div css={{ width: 12, height: 12, background: color }} />
            <div>
              {indexValue} {id}: {value}%
            </div>
          </div>
        ),
        axisLeft: {
          format: (value) => `${value}%`,
        },
        label: (node) => `${node.value}%`,
      }
    case 'horizontal-grouped-stacked':
      return {
        layout: 'horizontal',
        enableGridX: true,
        enableGridY: false,
        innerPadding: 2,
        margin: { right: 10, top: 10 },
        // eslint-disable-next-line react/prop-types
        tooltip: ({ indexValue, value, color, id }) => (
          <div
            css={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: 'auto',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div css={{ width: 12, height: 12, background: color }} />
            <div>
              {indexValue} {id}: {value}%
            </div>
          </div>
        ),
        axisLeft: {
          format: (value) => `${value}%`,
        },
        label: (node) => `${node.value}%`,
      }
    case 'vertical-grouped-stacked':
      return {
        layout: 'vertical',
        enableGridX: false,
        enableGridY: true,
        innerPadding: 2,
        margin: { left: 35, bottom: 10, top: 10 },
        // eslint-disable-next-line react/prop-types
        tooltip: ({ indexValue, value, color, id }) => (
          <div
            css={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: 'auto',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div css={{ width: 12, height: 12, background: color }} />
            <div>
              {indexValue} {id}: {value}%
            </div>
          </div>
        ),
        axisLeft: {
          format: (value) => `${value}%`,
        },
        label: (node) => `${node.value}%`,
      }
    case 'vertical-grouped':
      return {
        groupMode: 'grouped',
        layout: 'vertical',
        enableGridX: false,
        enableGridY: true,
        colorBy: 'index',
        innerPadding: 2,
        margin: { left: 30, bottom: 10, top: 10 },
        // eslint-disable-next-line react/prop-types
        tooltip: ({ indexValue, value, color, id }) => (
          <div
            css={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: 'auto',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div css={{ width: 12, height: 12, background: color }} />
            <div>
              {indexValue} {id}: {value}%
            </div>
          </div>
        ),
        axisLeft: {
          format: (value) => `${value}%`,
        },
        label: (node) => `${node.value}%`,
      }
  }
}
